import { useState } from 'react'
import { motion } from 'framer-motion'
import { Zap, CheckCircle, ChevronRight, Plus, History } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { cn } from '../lib/utils'

// ─── Types ────────────────────────────────────────────────────────────────────

type RuleStatus = 'Active' | 'Paused' | 'Draft'
type TriggerType = 'rent_late' | 'lease_expiring' | 'maintenance_sla' | 'vacancy' | 'payment_received' | 'maintenance_submitted'
type ActionType = 'send_sms' | 'send_email' | 'create_task' | 'add_late_fee' | 'notify_team'

type Rule = {
  id: string; name: string; description: string
  trigger: TriggerType; triggerLabel: string
  action: ActionType; actionLabel: string
  status: RuleStatus; runCount: number; lastRun: string
  category: 'payments' | 'leasing' | 'maintenance' | 'operations'
}

type HistoryEntry = {
  id: string; ruleName: string; trigger: string; action: string
  result: 'Success' | 'Failed' | 'Skipped'; timestamp: string; detail: string
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const INIT_RULES: Rule[] = [
  { id: 'r1', name: 'Late Rent Reminder – Day 3', description: 'Send SMS reminder when rent is 3 days overdue.', trigger: 'rent_late', triggerLabel: 'Rent overdue 3 days', action: 'send_sms', actionLabel: 'SMS to tenant', status: 'Active', runCount: 14, lastRun: '2026-07-04', category: 'payments' },
  { id: 'r2', name: 'Late Rent Reminder – Day 7', description: 'Send formal email notice at 7 days late. Attach late fee policy.', trigger: 'rent_late', triggerLabel: 'Rent overdue 7 days', action: 'send_email', actionLabel: 'Email formal notice', status: 'Active', runCount: 8, lastRun: '2026-07-01', category: 'payments' },
  { id: 'r3', name: 'Charge Late Fee – Day 5', description: 'Automatically add $50 late fee to tenant ledger at 5 days overdue.', trigger: 'rent_late', triggerLabel: 'Rent overdue 5 days', action: 'add_late_fee', actionLabel: 'Add $50 late fee', status: 'Active', runCount: 6, lastRun: '2026-07-03', category: 'payments' },
  { id: 'r4', name: 'Lease Expiry Alert – 90 Days', description: 'Notify PM when lease is 90 days from expiration to begin renewal process.', trigger: 'lease_expiring', triggerLabel: 'Lease expires in 90 days', action: 'create_task', actionLabel: 'Create renewal task', status: 'Active', runCount: 3, lastRun: '2026-04-01', category: 'leasing' },
  { id: 'r5', name: 'Lease Expiry Alert – 30 Days', description: 'Send final reminder email to tenant with lease renewal offer.', trigger: 'lease_expiring', triggerLabel: 'Lease expires in 30 days', action: 'send_email', actionLabel: 'Email renewal offer', status: 'Active', runCount: 2, lastRun: '2026-06-01', category: 'leasing' },
  { id: 'r6', name: 'Maintenance SLA Breach Alert', description: 'Alert team when an Urgent work order hasn\'t been assigned within 4 hours.', trigger: 'maintenance_sla', triggerLabel: 'Urgent WO unassigned 4h', action: 'notify_team', actionLabel: 'Notify property manager', status: 'Active', runCount: 2, lastRun: '2026-07-06', category: 'maintenance' },
  { id: 'r7', name: 'Vacancy Alert After 14 Days', description: 'Create marketing task when unit has been vacant for 14+ days.', trigger: 'vacancy', triggerLabel: 'Unit vacant 14 days', action: 'create_task', actionLabel: 'Create marketing task', status: 'Active', runCount: 1, lastRun: '2026-06-15', category: 'leasing' },
  { id: 'r8', name: 'Payment Received Confirmation', description: 'Send auto-receipt to tenant when rent payment is recorded.', trigger: 'payment_received', triggerLabel: 'Rent payment recorded', action: 'send_sms', actionLabel: 'SMS receipt to tenant', status: 'Paused', runCount: 22, lastRun: '2026-07-01', category: 'payments' },
  { id: 'r9', name: 'New Maintenance Request Ack', description: 'Auto-acknowledge tenant maintenance requests within 1 hour.', trigger: 'maintenance_submitted', triggerLabel: 'Maintenance request submitted', action: 'send_sms', actionLabel: 'SMS acknowledgment', status: 'Draft', runCount: 0, lastRun: '—', category: 'maintenance' },
]

const HISTORY: HistoryEntry[] = [
  { id: 'h1', ruleName: 'Late Rent Reminder – Day 3', trigger: 'Rent 3 days late – Avery Kim', action: 'SMS sent to +1-555-0192', result: 'Success', timestamp: '2026-07-04 09:00', detail: 'Message delivered. Tenant viewed 09:07.' },
  { id: 'h2', ruleName: 'Charge Late Fee – Day 5', trigger: 'Rent 5 days late – Avery Kim', action: 'Late fee $50 added to ledger', result: 'Success', timestamp: '2026-07-03 09:00', detail: 'Fee added. Balance now $3,150.' },
  { id: 'h3', ruleName: 'Maintenance SLA Breach Alert', trigger: 'Urgent WO #wo1 unassigned 4h', action: 'Notify PM via email', result: 'Success', timestamp: '2026-07-06 14:00', detail: 'Email sent to pm@xlproperty.com.' },
  { id: 'h4', ruleName: 'Late Rent Reminder – Day 7', trigger: 'Rent 7 days late – Avery Kim', action: 'Email formal notice', result: 'Success', timestamp: '2026-07-01 09:00', detail: 'Email delivered. No response yet.' },
  { id: 'h5', ruleName: 'Vacancy Alert After 14 Days', trigger: 'Unit 2A vacant 14 days', action: 'Create marketing task', result: 'Success', timestamp: '2026-06-15 09:00', detail: 'Task #t-024 created and assigned to leasing team.' },
  { id: 'h6', ruleName: 'Lease Expiry Alert – 90 Days', trigger: 'Jordan Blake lease in 90 days', action: 'Create renewal task', result: 'Success', timestamp: '2026-04-01 09:00', detail: 'Renewal task created and assigned.' },
]

const CATEGORY_STYLE: Record<string, string> = {
  payments: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-800',
  leasing: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/30 dark:text-violet-300 dark:border-violet-800',
  maintenance: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800',
  operations: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800',
}

const STATUS_STYLE: Record<RuleStatus, string> = {
  Active: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800',
  Paused: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800',
  Draft: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-600',
}

// ─── Rules List ───────────────────────────────────────────────────────────────

function RulesTab() {
  const [rules, setRules] = useState<Rule[]>(INIT_RULES)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [flash, setFlash] = useState<string | null>(null)

  const toggle = (id: string) => {
    setRules(prev => prev.map(r => {
      if (r.id !== id) return r
      return { ...r, status: r.status === 'Active' ? 'Paused' : 'Active' }
    }))
  }

  const activeCount = rules.filter(r => r.status === 'Active').length
  const totalRuns = rules.reduce((s, r) => s + r.runCount, 0)

  const handleAddRule = () => {
    const id = `r${Date.now()}`
    const newRule: Rule = {
      id,
      name: 'New Follow-Up Rule',
      description: 'Draft rule created. Edit trigger and action details.',
      trigger: 'maintenance_submitted',
      triggerLabel: 'Maintenance request submitted',
      action: 'notify_team',
      actionLabel: 'Notify property manager',
      status: 'Draft',
      runCount: 0,
      lastRun: '—',
      category: 'operations',
    }
    setRules((prev) => [newRule, ...prev])
    setExpanded(id)
    setFlash('New draft rule added.')
  }

  const handleEditRule = (id: string) => {
    setRules((prev) =>
      prev.map((rule) => {
        if (rule.id !== id) {
          return rule
        }
        const nextName = rule.name.includes('(Edited)') ? rule.name : `${rule.name} (Edited)`
        return { ...rule, name: nextName, status: 'Draft' }
      }),
    )
    setFlash('Rule moved to Draft for editing.')
  }

  const handleDeleteRule = (id: string) => {
    setRules((prev) => prev.filter((rule) => rule.id !== id))
    if (expanded === id) {
      setExpanded(null)
    }
    setFlash('Rule deleted.')
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <Card className="p-3 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Active Rules</p>
          <p className="mt-1.5 text-2xl font-bold text-emerald-700 dark:text-emerald-400">{activeCount}</p>
        </Card>
        <Card className="p-3 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Total Runs</p>
          <p className="mt-1.5 text-2xl font-bold text-slate-900 dark:text-slate-100">{totalRuns}</p>
          <p className="text-xs text-slate-500 mt-1">All time</p>
        </Card>
        <Card className="p-3 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Hours Saved</p>
          <p className="mt-1.5 text-2xl font-bold text-violet-700 dark:text-violet-400">~{Math.round(totalRuns * 0.4)}h</p>
          <p className="text-xs text-slate-500 mt-1">Estimated</p>
        </Card>
        <Card className="p-3 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Paused / Draft</p>
          <p className="mt-1.5 text-2xl font-bold text-amber-700 dark:text-amber-400">{rules.length - activeCount}</p>
        </Card>
      </div>

      {flash && (
        <Card className="p-3 shadow-sm border-emerald-200 bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-950/25">
          <p className="text-xs text-emerald-800 dark:text-emerald-300">{flash}</p>
        </Card>
      )}

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Automation Rules</CardTitle>
          <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" onClick={handleAddRule}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />New Rule
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {rules.map(rule => (
              <div key={rule.id}>
                <div className="flex flex-wrap items-start gap-3 px-4 py-3.5 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{rule.name}</p>
                      <span className={cn('rounded-full border px-2 py-0.5 text-[10px] font-bold', CATEGORY_STYLE[rule.category])}>{rule.category}</span>
                      <span className={cn('rounded-full border px-2 py-0.5 text-[10px] font-bold', STATUS_STYLE[rule.status])}>{rule.status}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{rule.description}</p>
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-[11px] text-slate-500">
                      <span className="flex items-center gap-1"><Zap className="h-3 w-3 text-amber-500" />{rule.triggerLabel}</span>
                      <span>→</span>
                      <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-emerald-500" />{rule.actionLabel}</span>
                      <span className="text-slate-400">· {rule.runCount} runs · Last: {rule.lastRun}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => setExpanded(expanded === rule.id ? null : rule.id)}
                      className="text-slate-400 hover:text-violet-600 transition-colors">
                      <ChevronRight className={cn('h-4 w-4 transition-transform', expanded === rule.id && 'rotate-90')} />
                    </button>
                    <button
                      onClick={() => toggle(rule.id)}
                      className={cn(
                        'relative h-6 w-11 rounded-full transition-all duration-200 focus:outline-none',
                        rule.status === 'Active' ? 'bg-violet-600' : 'bg-slate-300 dark:bg-slate-600'
                      )}
                    >
                      <span className={cn(
                        'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-all duration-200',
                        rule.status === 'Active' ? 'left-[22px]' : 'left-0.5'
                      )} />
                    </button>
                  </div>
                </div>
                <AnimatedDetail
                  open={expanded === rule.id}
                  rule={rule}
                  onEdit={() => handleEditRule(rule.id)}
                  onDelete={() => handleDeleteRule(rule.id)}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function AnimatedDetail({
  open,
  rule,
  onEdit,
  onDelete,
}: {
  open: boolean
  rule: Rule
  onEdit: () => void
  onDelete: () => void
}) {
  if (!open) return null
  return (
    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
      className="bg-violet-50/50 dark:bg-violet-950/10 border-t border-violet-100 dark:border-violet-900 px-4 py-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 text-xs">
        <div>
          <p className="font-semibold text-violet-700 dark:text-violet-300 uppercase tracking-wide mb-1">Trigger</p>
          <div className="rounded-lg border border-violet-200 dark:border-violet-800 bg-white dark:bg-slate-900 p-2.5">
            <p className="font-medium text-slate-800 dark:text-slate-200">When: {rule.triggerLabel}</p>
          </div>
        </div>
        <div>
          <p className="font-semibold text-violet-700 dark:text-violet-300 uppercase tracking-wide mb-1">Action</p>
          <div className="rounded-lg border border-violet-200 dark:border-violet-800 bg-white dark:bg-slate-900 p-2.5">
            <p className="font-medium text-slate-800 dark:text-slate-200">Then: {rule.actionLabel}</p>
          </div>
        </div>
        <div>
          <p className="font-semibold text-violet-700 dark:text-violet-300 uppercase tracking-wide mb-1">Stats</p>
          <div className="rounded-lg border border-violet-200 dark:border-violet-800 bg-white dark:bg-slate-900 p-2.5 space-y-1">
            <p className="text-slate-600 dark:text-slate-400">Runs: <span className="font-bold text-slate-900 dark:text-slate-100">{rule.runCount}</span></p>
            <p className="text-slate-600 dark:text-slate-400">Last: <span className="font-bold text-slate-900 dark:text-slate-100">{rule.lastRun}</span></p>
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <Button size="sm" variant="secondary" className="text-xs h-7" onClick={onEdit}>Edit Rule</Button>
        <Button size="sm" variant="secondary" className="text-xs h-7 text-rose-600 hover:text-rose-700" onClick={onDelete}>Delete</Button>
      </div>
    </motion.div>
  )
}

// ─── History Tab ──────────────────────────────────────────────────────────────

function HistoryTab() {
  const successCount = HISTORY.filter(h => h.result === 'Success').length

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-3">
        <Card className="p-3 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Total Executions</p>
          <p className="mt-1.5 text-2xl font-bold text-slate-900 dark:text-slate-100">{HISTORY.length}</p>
          <p className="text-xs text-slate-500 mt-1">Last 30 days</p>
        </Card>
        <Card className="p-3 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Success Rate</p>
          <p className="mt-1.5 text-2xl font-bold text-emerald-700 dark:text-emerald-400">{Math.round((successCount / HISTORY.length) * 100)}%</p>
        </Card>
        <Card className="p-3 shadow-sm col-span-2 xl:col-span-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Last 7 Days</p>
          <p className="mt-1.5 text-2xl font-bold text-violet-700 dark:text-violet-400">{HISTORY.filter(h => h.timestamp > '2026-07-01').length}</p>
        </Card>
      </div>

      <Card className="shadow-sm">
        <CardHeader><CardTitle className="text-base">Execution Log</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {HISTORY.map(h => (
              <div key={h.id} className="flex flex-wrap items-start gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                <div className={cn('mt-0.5 h-2 w-2 rounded-full shrink-0', h.result === 'Success' ? 'bg-emerald-500' : h.result === 'Failed' ? 'bg-rose-500' : 'bg-slate-400')} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{h.ruleName}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{h.trigger}</p>
                  <p className="text-xs text-slate-400 mt-0.5">→ {h.action}</p>
                  <p className="text-[11px] text-slate-400 mt-1 italic">{h.detail}</p>
                </div>
                <div className="text-right shrink-0">
                  <span className={cn('rounded-full border px-2 py-0.5 text-[10px] font-bold',
                    h.result === 'Success' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800' :
                    h.result === 'Failed' ? 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-800' :
                    'bg-slate-100 text-slate-500 border-slate-200'
                  )}>{h.result}</span>
                  <p className="text-[10px] text-slate-400 mt-1">{h.timestamp}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ─── Main Export ──────────────────────────────────────────────────────────────

type AutoTab = 'rules' | 'history'

export default function AutomationModule() {
  const [tab, setTab] = useState<AutoTab>('rules')

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">Automation</h1>
        <p className="text-xs text-slate-500 dark:text-slate-400">Rules engine for reminders, alerts, and automated workflows</p>
      </div>

      <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1">
        {([['rules', Zap, 'Rules'] as const, ['history', History, 'Execution Log'] as const]).map(([id, Icon, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={cn(
              'flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all',
              tab === id ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm' : 'text-slate-600 dark:text-slate-400 hover:bg-white/60'
            )}>
            <Icon className="h-4 w-4" />{label}
          </button>
        ))}
      </div>

      <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }}>
        {tab === 'rules' ? <RulesTab /> : <HistoryTab />}
      </motion.div>
    </div>
  )
}
